import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Mail, Facebook, Instagram, Send } from "lucide-react";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", message: "" };

const inputClass =
  "w-full border-b border-[#E2DFD8] bg-transparent py-3 text-base outline-none transition-colors duration-300 placeholder:text-[#5C5C5A]/50 focus:border-[#C85C40]";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Message sent — thank you!", {
        description: "We'll get back to you shortly. Fresh laundry awaits.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or reach us by phone or social media.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="mx-auto max-w-7xl scroll-mt-28 px-6 py-32 lg:px-10 lg:py-40"
    >
      <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#C85C40]">
              Say hello
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Come spin <span className="italic text-[#C85C40]">with us</span>.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#5C5C5A] md:text-lg">
              Questions about wash &amp; fold, pickup &amp; delivery, or the
              upcoming app? We'd love to hear from you.
            </p>

            <div className="mt-12 flex flex-col gap-6">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C85C40]/10 text-[#C85C40]">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-bold">Visit us</p>
                  <p className="text-sm leading-relaxed text-[#5C5C5A]">
                    6980 Indian Head Highway
                    <br />
                    Bryans Road, Maryland 20616
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#7D8C74]/15 text-[#7D8C74]">
                  <Clock className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-bold">Hours</p>
                  <p className="text-sm leading-relaxed text-[#5C5C5A]">
                    Mon, Tue, Thu, Fri, Sat · 8:00 AM – 8:00 PM
                    <br />
                    Sunday · 8:00 AM – 6:00 PM · Closed Wednesday
                    <br />
                    Last wash · 6:30 PM (Sun 4:30 PM)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C85C40]/10 text-[#C85C40]">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-bold">Call us</p>
                  <a
                    href="tel:+12403837489"
                    data-testid="contact-phone-link"
                    className="text-sm text-[#5C5C5A] transition-colors duration-300 hover:text-[#C85C40]"
                  >
                    (240) 383-7489
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#7D8C74]/15 text-[#7D8C74]">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-bold">Email</p>
                  <a
                    href="mailto:service@spintasticcity.com"
                    data-testid="contact-email-link"
                    className="text-sm text-[#5C5C5A] transition-colors duration-300 hover:text-[#C85C40]"
                  >
                    service@spintasticcity.com
                  </a>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-3" data-testid="contact-socials">
                <a
                  href="https://www.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  aria-label="Spintastic City on Facebook — page coming soon"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DFD8] text-[#5C5C5A] transition-[background-color,color,border-color] duration-300 hover:border-[#C85C40] hover:bg-[#C85C40] hover:text-white"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  aria-label="Spintastic City on Instagram — page coming soon"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DFD8] text-[#5C5C5A] transition-[background-color,color,border-color] duration-300 hover:border-[#C85C40] hover:bg-[#C85C40] hover:text-white"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[24px] border border-[#E2DFD8]/60 bg-white/70 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md lg:p-12"
            >
              <h3 className="font-serif text-2xl tracking-tight sm:text-3xl">
                Send us a message
              </h3>
              <div className="mt-8 grid gap-8 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-widest text-[#5C5C5A]">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email-input" className="text-xs font-bold uppercase tracking-widest text-[#5C5C5A]">
                    Email
                  </label>
                  <input
                    id="contact-email-input"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-phone-input" className="text-xs font-bold uppercase tracking-widest text-[#5C5C5A]">
                    Phone <span className="font-medium normal-case tracking-normal">(optional)</span>
                  </label>
                  <input
                    id="contact-phone-input"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(240) 555-0000"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message-input" className="text-xs font-bold uppercase tracking-widest text-[#5C5C5A]">
                    Message
                  </label>
                  <textarea
                    id="contact-message-input"
                    data-testid="contact-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="How can we help?"
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#C85C40] px-8 py-4 text-sm font-bold text-white transition-[background-color,transform,opacity] duration-300 hover:scale-[0.98] hover:bg-[#A84C34] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Message"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>

      <FadeUp delay={0.1} className="mt-24">
        <div
          className="overflow-hidden rounded-[2rem] border border-[#E2DFD8]/60 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
          data-testid="contact-map-frame"
        >
          <iframe
            title="Spintastic City Laundry Services — 6980 Indian Head Highway, Bryans Road, MD 20616"
            data-testid="contact-map"
            src="https://maps.google.com/maps?q=6980%20Indian%20Head%20Highway%2C%20Bryans%20Road%2C%20MD%2020616&z=14&output=embed"
            className="h-[420px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </FadeUp>
    </section>
  );
}
